// 원장 호출 — Firestore 경로·쓰기·callable (2026-10-08). 서버 계약 정본: docs/계약_원장호출_서버_2026-10-08.md
//  - 호출·응답·해제·데스크 기록 = 클라이언트가 Firestore 에 직접 쓴다. rules 가 값·전이·시각(== request.time)을 검증
//  - doctorCalls/{clinicId}                       설정(대표만) + present(재실, 대표·직원)
//  - doctorCalls/{clinicId}/callCells/{chair}_{situation}   칸별 현재 호출 1건(13키 정확히, 계약 §4)
//  - callLogs 는 클라이언트 접근 불가(CF 전용) — 주간 집계는 getDoctorCallWeeklyStats(대표만)
//  - 저장 = 체어 번호·상황 키·시각·응답 키·데스크 시각뿐. 환자·직원 이름·자유 문구 0
//  경로·필드는 이 파일 한 곳에서만 정한다(화면은 이 함수들만 부른다)
import { collection, doc, onSnapshot, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { SITUATION_KEYS, ACK_KEYS, CHAIRS_MAX, ackOptions, normalizeSettings } from '../utils/doctorCall';

// region 은 다른 치과 callable 관례(receptionApi·clinicWalkinApi)와 같게 — 계약에 명시 없음(서버 코드 확인 후 맞출 것)
const REGION = 'asia-northeast3';
const callable = (name) => httpsCallable(getFunctions(undefined, REGION), name);

const clinicRef = (clinicId) => doc(db, 'doctorCalls', clinicId);
const cellsCol = (clinicId) => collection(db, 'doctorCalls', clinicId, 'callCells');
const cellRef = (clinicId, chair, situation) => doc(db, 'doctorCalls', clinicId, 'callCells', `${chair}_${situation}`);

const bad = (msg) => Object.assign(new Error(msg), { code: 'invalid-argument' });

// 칸 문서 → 화면 모델(utils/doctorCall 이 읽는 모양)
function toCall(id, d, pending) {
    return {
        id: d.callId || id,
        cellId: id,
        chairNo: Number(d.chair),
        situation: d.situation,
        state: d.status,
        createdAt: d.createdAt || null,
        ack: d.ackType ? { type: d.ackType, min: Number(d.ackMin) || 0, at: d.ackAt || null } : null,
        clearedAt: d.endedAt || null,
        endReason: d.endReason || null,
        deskCalledAt: d.deskCalledAt || null,
        deskVisitorAt: d.deskVisitorAt || null,
        _pending: !!pending,
    };
}

// 치과 문서 구독 — onData({ settings, present(true|false|null), exists }, metadata)
export function subscribeClinic(clinicId, onData, onError) {
    return onSnapshot(clinicRef(clinicId), { includeMetadataChanges: true }, (snap) => {
        const d = snap.exists() ? snap.data({ serverTimestamps: 'estimate' }) : {};
        onData({ settings: normalizeSettings(d.settings), present: typeof d.present === 'boolean' ? d.present : null, exists: snap.exists() }, snap.metadata);
    }, onError);
}

// 칸 전체 구독(≤48문서, 계약 §4-2 — 쿼리 조건 없이 전체)
export function subscribeCells(clinicId, onData, onError) {
    return onSnapshot(cellsCol(clinicId), { includeMetadataChanges: true }, (snap) => {
        const list = snap.docs.map(d => toCall(d.id, d.data({ serverTimestamps: 'estimate' }), d.metadata.hasPendingWrites));
        onData(list, snap.metadata);
    }, onError);
}

// ① 호출 — merge 없이 13키 전체. callId 는 새 자동 id
export async function openCall(clinicId, chair, situation) {
    if (!Number.isInteger(chair) || chair < 1 || chair > CHAIRS_MAX) throw bad('bad chair');
    if (!SITUATION_KEYS.includes(situation)) throw bad('bad situation');
    const callId = doc(collection(db, '_')).id;
    await setDoc(cellRef(clinicId, chair, situation), {
        chair, situation, callId, status: 'open',
        createdAt: serverTimestamp(),
        ackType: null, ackMin: null, ackAt: null,
        endedAt: null, endReason: null,
        deskCalledAt: null, deskVisitorAt: null,
        updatedAt: serverTimestamp(),
    });
    return callId;
}

// ② 응답(원장) — open 또는 acked(응답 변경). ackMin 은 현재 ackSet 표 값(rules 가 같은 표로 검사)
export async function ackCall(clinicId, call, type, ackSet) {
    if (!ACK_KEYS.includes(type)) throw bad('bad ack');
    const o = ackOptions(ackSet).find(x => x.key === type);
    return updateDoc(doc(cellsCol(clinicId), call.cellId), {
        status: 'acked', ackType: type, ackMin: o ? o.min : 0, ackAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
}

// ③ 해제(진료실 칸 다시 한 번)
export async function clearCall(clinicId, call) {
    return updateDoc(doc(cellsCol(clinicId), call.cellId), {
        status: 'cleared', endedAt: serverTimestamp(), endReason: 'manual', updatedAt: serverTimestamp(),
    });
}

// ④ 응답 시간 지남 — 서버 시각 ≥ ackAt + ackMin분 이어야 rules 통과(일찍 보내면 거부)
export async function expireCall(clinicId, call) {
    return updateDoc(doc(cellsCol(clinicId), call.cellId), {
        status: 'expired', endedAt: serverTimestamp(), endReason: 'ack_elapsed', updatedAt: serverTimestamp(),
    });
}

// ⑤ 데스크 기록 — 'called'(원장실로 전화함 ✓ — 기록만, 전화를 걸지 않음) | 'visitor'(방문객 있음). 호출을 만들거나 끄지 않는다
export async function deskAction(clinicId, call, type) {
    const field = type === 'called' ? 'deskCalledAt' : type === 'visitor' ? 'deskVisitorAt' : null;
    if (!field) throw bad('bad desk action');
    return updateDoc(doc(cellsCol(clinicId), call.cellId), { [field]: serverTimestamp(), updatedAt: serverTimestamp() });
}

// 원장실 재실/부재 (대표·직원) — 바뀌는 키 present·presentUpdatedAt 뿐
export async function setPresent(clinicId, present) {
    return setDoc(clinicRef(clinicId), { present: !!present, presentUpdatedAt: serverTimestamp() }, { merge: true });
}

// 설정 저장 (대표만) — settings 6키 통째로
export async function saveSettings(clinicId, settings) {
    const s = normalizeSettings(settings);
    return setDoc(clinicRef(clinicId), {
        settings: { chairs: s.chairs, labels: { ...s.labels }, ackSet: s.ackSet, soundUpSec: s.soundUpSec, deskShowSec: s.deskShowSec, urgentColor: s.urgentColor },
        settingsUpdatedAt: serverTimestamp(),
    }, { merge: true });
}

// 서버 시각 — { nowMs }
export async function getDoctorCallClock() {
    const r = await callable('getDoctorCallClock')({});
    return r?.data || {};
}

// 주간 집계(대표만) — weekStart 'YYYY-MM-DD'(KST 월요일) 없으면 이번 주
export async function getDoctorCallWeeklyStats(weekStart) {
    const r = await callable('getDoctorCallWeeklyStats')(weekStart ? { weekStart } : {});
    return r?.data || {};
}

// 📱 받는 기기(휴대폰 덴클 앱·원장실 PC 프로그램) — 대표만(대표 10-13 Q8). 토큰·기기 id 는 오지 않는다(key = 서버 해시)
export async function listDoctorCallReceivers() {
    const r = await callable('listDoctorCallReceivers')({});
    return r.data || { receivers: [], max: 10 };
}
export async function removeDoctorCallReceivers(arg) {   // { key } 또는 { all: true }
    const r = await callable('removeDoctorCallReceivers')(arg);
    return r.data || { removed: 0 };
}
